import {
  ChecklistDTO,
  SupportingItemInputDTO,
  SupportingItemInputTypeDiscriminatorDTO,
  SectionDTO,
  RequestedSupportingItemDTO,
  DocumentSupportingItemInputTypeDTO,
} from './dtos/info-gathering'
import { Model } from '../infra/model'
import { observable, makeObservable, computed } from 'mobx'
import { collection } from '../infra/collection'
import { maybeParseDate, DateZero } from '../utils/date-util'
import { PrimeDocument } from '../documents/DocumentStore'
import { isNotNil } from '../utils/object-util'
import Document from '../documents/Document'
import DocumentType from '../documents/DocumentType'
import { PrimeDocumentTypeByShortId } from '../documents/DocumentTypeStore'
import orderBy from 'lodash/orderBy'

export default class Checklist extends Model<ChecklistDTO, Checklist> {
  jobId: string = ''
  sections = collection<SectionDTO, Section>({
    create: () => new Section(this.primeDocument, this.primeDocumentType),
  })

  constructor(
    private readonly primeDocument: PrimeDocument,
    private readonly primeDocumentType: PrimeDocumentTypeByShortId
  ) {
    super()
    makeObservable(this, {
      jobId: observable,
      sections: observable,
    })
  }

  /**
   * Gets the requested item by ID.
   *
   * @param id ID of the item to get.
   */
  getItemById(id: string) {
    for (const section of this.sections.items) {
      for (const item of section.requestedSupportingItems.items) {
        if (item.id === id) {
          return item
        }
      }
    }
    return null
  }

  parse(json: ChecklistDTO): Partial<Checklist> {
    return {
      jobId: json.job_id,
      sections: this.sections.reset(json.sections),
    }
  }
}

export class Section extends Model<SectionDTO, Section> {
  id: string = ''
  name: string = ''
  heading: string = ''
  description: string = ''
  requestedSupportingItems = collection<
    RequestedSupportingItemDTO,
    RequestedSupportingItem
  >({
    create: () =>
      new RequestedSupportingItem(this.primeDocument, this.primeDocumentType),
  })

  constructor(
    private readonly primeDocument: PrimeDocument,
    private readonly primeDocumentType: PrimeDocumentTypeByShortId
  ) {
    super()
    makeObservable(this, {
      id: observable,
      name: observable,
      heading: observable,
      description: observable,
      requestedSupportingItems: observable,
      requirementsCompleted: computed,
      completedCount: computed,
      totalCount: computed,
    })
  }

  get requirementsCompleted(): boolean {
    return this.requestedSupportingItems.every(
      (requestedSupportingItem) =>
        requestedSupportingItem.required && requestedSupportingItem.completed
    )
  }

  get completedCount(): number {
    return this.requestedSupportingItems.filter(
      (requestedSupportingItem) => requestedSupportingItem.completed
    ).length
  }

  get totalCount(): number {
    return this.requestedSupportingItems.length
  }

  parse(json: SectionDTO) {
    const itemDtos =
      json.requested_supporting_items &&
      orderBy(json.requested_supporting_items, (x) => x.required, 'desc')

    return {
      id: json.id,
      name: json.name,
      heading: json.heading,
      description: json.description,
      requestedSupportingItems: this.requestedSupportingItems.reset(itemDtos),
    }
  }
}

export class RequestedSupportingItem extends Model<
  RequestedSupportingItemDTO,
  RequestedSupportingItem
> {
  id: string = ''
  name: string = ''
  heading: string = ''
  shortDescription: string = ''
  longDescription: string = ''
  imageUrl?: string = ''
  previewUrl?: string = ''
  required: boolean = false
  completed: boolean = false
  dateModified: Date = DateZero()
  input: SupportingItemInput | null = null

  constructor(
    private readonly primeDocument: PrimeDocument,
    private readonly primeDocumentType: PrimeDocumentTypeByShortId
  ) {
    super()
    makeObservable(this, {
      id: observable,
      name: observable,
      heading: observable,
      shortDescription: observable,
      longDescription: observable,
      imageUrl: observable,
      previewUrl: observable,
      required: observable,
      completed: observable,
      input: observable,
    })
  }

  parse(json: RequestedSupportingItemDTO) {
    return {
      id: json.id,
      name: json.name,
      heading: json.heading,
      shortDescription: json.short_description,
      longDescription: json.long_description,
      imageUrl: json.image_url,
      previewUrl: json.preview_url,
      required: json.required,
      completed: json.completed,
      dateModified: maybeParseDate(json.update_time),
      input: parseSupportingItemInputType(
        json.input,
        this.primeDocument,
        this.primeDocumentType
      ),
    }
  }
}

function parseSupportingItemInputType(
  input: SupportingItemInputDTO,
  primeDocument: PrimeDocument,
  primeDocumentType: PrimeDocumentTypeByShortId
): SupportingItemInput | undefined {
  switch (input.type.discriminator) {
    case SupportingItemInputTypeDiscriminatorDTO.DOCUMENT:
      return new DocumentSupportingItemInput(
        primeDocument,
        primeDocumentType
      ).set(input.type, { parse: true, stripUndefined: true })
  }
}

export type SupportingItemInput = DocumentSupportingItemInput

export class DocumentSupportingItemInput extends Model<
  DocumentSupportingItemInputTypeDTO,
  DocumentSupportingItemInput
> {
  documentType = DocumentType.none
  documents: Document[] = []

  constructor(
    private readonly primeDocument: PrimeDocument,
    private readonly primeDocumentType: PrimeDocumentTypeByShortId
  ) {
    super()
    makeObservable(this, {
      documentType: observable,
      documents: observable,
    })
  }

  parse(json: Partial<DocumentSupportingItemInputTypeDTO>) {
    const documents = json.document?.document_ids
      .map((id) => this.primeDocument({ id }))
      .filter(isNotNil)
    return {
      documentType: json.document?.document_type_short_id
        ? this.primeDocumentType(json.document?.document_type_short_id)
        : undefined,
      documents,
    }
  }
}
